import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { sha256, uid, genCode, generateSessionToken, constantTimeCompare, verifyAuditChain } from "../utils/crypto";
import { saveData, loadData, saveLockout, loadLockout } from "../utils/storage";
import { sanitize, validAmount, validDate, isDuplicateExpense, isThrottled, today, currentMonth } from "../utils/helpers";
import {
  ADMIN_HASHES, MAX_LOGIN_ATTEMPTS, LOCKOUT_DURATION_MS,
  SESSION_TIMEOUT_MS, SESSION_CHECK_INTERVAL, MAX_PENDING_LOANS,
  MAX_AUDIT_ENTRIES, SALARY_LOAN_MULTIPLIER, MAX_NAME_LENGTH
} from "../utils/constants";

const Ctx = createContext();
export const useApp = () => useContext(Ctx);


const DEFAULT_CRYPTO_HOLDINGS = [
  { id:"h0", coin:"inr-fiat", symbol:"INR", name:"Indian Rupee", amount:50000, priceAtReceipt:1, source:"Manual Deposit", dateReceived:"2026-02-16" },
  { id:"h1", coin:"bitcoin", symbol:"BTC", name:"Bitcoin", amount:0.0005, priceAtReceipt:7200000, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h2", coin:"ethereum", symbol:"ETH", name:"Ethereum", amount:0.01, priceAtReceipt:220000, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h3", coin:"litecoin", symbol:"LTC", name:"Litecoin", amount:0.1, priceAtReceipt:8500, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h4", coin:"tether", symbol:"USDT", name:"Tether", amount:950, priceAtReceipt:84, source:"Stake Payout", dateReceived:"2026-02-16" },
  { id:"h5", coin:"solana", symbol:"SOL", name:"Solana", amount:0.5, priceAtReceipt:15000, source:"Airdrop", dateReceived:"2026-02-16" },
  { id:"h6", coin:"dogecoin", symbol:"DOGE", name:"Dogecoin", amount:500, priceAtReceipt:18, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h7", coin:"bitcoin-cash", symbol:"BCH", name:"Bitcoin Cash", amount:0.02, priceAtReceipt:32000, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h8", coin:"ripple", symbol:"XRP", name:"XRP", amount:50, priceAtReceipt:200, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h9", coin:"tron", symbol:"TRX", name:"TRON", amount:1000, priceAtReceipt:10, source:"Stake Payout", dateReceived:"2026-02-16" },
  { id:"h10", coin:"eos", symbol:"EOS", name:"EOS", amount:20, priceAtReceipt:65, source:"Airdrop", dateReceived:"2026-02-16" },
  { id:"h11", coin:"binancecoin", symbol:"BNB", name:"BNB", amount:0.1, priceAtReceipt:52000, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h12", coin:"usd-coin", symbol:"USDC", name:"USD Coin", amount:500, priceAtReceipt:84, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h13", coin:"apecoin", symbol:"APE", name:"ApeCoin", amount:10, priceAtReceipt:80, source:"Airdrop", dateReceived:"2026-02-16" },
  { id:"h14", coin:"binance-usd", symbol:"BUSD", name:"Binance USD", amount:200, priceAtReceipt:84, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h15", coin:"crypto-com-chain", symbol:"CRO", name:"Cronos", amount:500, priceAtReceipt:7, source:"Stake Payout", dateReceived:"2026-02-16" },
  { id:"h16", coin:"dai", symbol:"DAI", name:"Dai", amount:300, priceAtReceipt:84, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h17", coin:"chainlink", symbol:"LINK", name:"Chainlink", amount:5, priceAtReceipt:1200, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h18", coin:"the-sandbox", symbol:"SAND", name:"The Sandbox", amount:100, priceAtReceipt:28, source:"Airdrop", dateReceived:"2026-02-16" },
  { id:"h19", coin:"shiba-inu", symbol:"SHIB", name:"Shiba Inu", amount:1000000, priceAtReceipt:0.001, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h20", coin:"uniswap", symbol:"UNI", name:"Uniswap", amount:3, priceAtReceipt:600, source:"Airdrop", dateReceived:"2026-02-16" },
  { id:"h21", coin:"matic-network", symbol:"POL", name:"Polygon", amount:100, priceAtReceipt:35, source:"Purchase", dateReceived:"2026-02-16" },
  { id:"h22", coin:"official-trump", symbol:"TRUMP", name:"TRUMP", amount:5, priceAtReceipt:1400, source:"Purchase", dateReceived:"2026-02-16" },
];

export function AppProvider({ children }) {
  const [accessCode, setAccessCode] = useState("");
  const [mode, setMode] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [auditIntegrity, setAuditIntegrity] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [expenses, setExpenses] = useState([]);
  const [team, setTeam] = useState([]);
  const [cryptoHoldings, setCryptoHoldings] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loanRequests, setLoanRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [currency, setCurrency] = useState("inr");

  const lastActivityRef = useRef(Date.now());
  const sessionRef = useRef(null);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ message: String(msg).replace(/[<>&]/g, "").slice(0, 200), type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // INIT — Load all data (same encryption key for everyone)
  useEffect(() => {
    async function init() {
      const [se, st, sc, sa, sl, sp] = await Promise.all([
        loadData("expenses"), loadData("team"), loadData("crypto"),
        loadData("audit"), loadData("loans"), loadData("payments"),
      ]);
      if (se) setExpenses(se);
      if (st) setTeam(st);
      if (sc) setCryptoHoldings(sc);
      if (sa) { setAuditLog(sa); setAuditIntegrity(await verifyAuditChain(sa)); }
      if (sl) setLoanRequests(sl);
      if (sp) setPayments(sp);
      setIsLoading(false);
    }
    init();
  }, []);

  // PERSIST
  useEffect(() => { if (sessionRef.current) saveData("expenses", expenses); }, [expenses]);
  useEffect(() => { if (sessionRef.current) saveData("team", team); }, [team]);
  useEffect(() => { if (sessionRef.current) saveData("crypto", cryptoHoldings); }, [cryptoHoldings]);
  useEffect(() => { if (sessionRef.current) saveData("loans", loanRequests); }, [loanRequests]);
  useEffect(() => { if (sessionRef.current) saveData("payments", payments); }, [payments]);
  useEffect(() => {
    if (sessionRef.current) {
      const t = auditLog.length > MAX_AUDIT_ENTRIES ? auditLog.slice(-MAX_AUDIT_ENTRIES) : auditLog;
      saveData("audit", t);
    }
  }, [auditLog]);

  // SESSION TIMEOUT
  useEffect(() => {
    const i = setInterval(() => {
      if (sessionRef.current && Date.now() - lastActivityRef.current > SESSION_TIMEOUT_MS) doLogout();
    }, SESSION_CHECK_INTERVAL);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TRACK ACTIVITY
  useEffect(() => {
    const h = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener("click", h); window.addEventListener("keydown", h);
    return () => { window.removeEventListener("click", h); window.removeEventListener("keydown", h); };
  }, []);

  // TAB VISIBILITY
  useEffect(() => {
    const h = () => {
      if (document.visibilityState === "visible" && sessionRef.current) {
        if (Date.now() - lastActivityRef.current > SESSION_TIMEOUT_MS) doLogout();
        else lastActivityRef.current = Date.now();
      }
    };
    document.addEventListener("visibilitychange", h);
    return () => document.removeEventListener("visibilitychange", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ESCAPE KEY
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") window.dispatchEvent(new CustomEvent("closeModals")); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // AUDIT
  const addAuditEntry = useCallback(async (action, details = {}) => {
    const prev = auditLog.length > 0 ? auditLog[auditLog.length - 1] : null;
    const prevHash = prev ? await sha256(JSON.stringify(prev)) : "GENESIS";
    setAuditLog(p => [...p, { id: uid(), timestamp: new Date().toISOString(), action, actor: currentUser || "System", details, prevHash }]);
  }, [auditLog, currentUser]);

  // LOGOUT — wipes state from memory
  const doLogout = useCallback(() => {
    setSessionToken(null); sessionRef.current = null;
    setMode(null); setAccessCode(""); setCurrentUser(null); setCurrentUserId(null);
  }, []);

  const logout = useCallback(() => {
    addAuditEntry("AUTH_LOGOUT", { reason: "Manual" });
    setTimeout(doLogout, 100);
  }, [addAuditEntry, doLogout]);

  // LOGIN — works for admin AND employees with same encryption key
  const login = async (code) => {
    const lockout = await loadLockout();
    if (lockout && lockout.lockUntil > Date.now()) {
      setLoginError("Locked. " + Math.ceil((lockout.lockUntil - Date.now()) / 1000) + "s remaining");
      return false;
    }

    const codeHash = await sha256(code);

    // Check admin hashes (constant-time)
    let adminIndex = -1;
    for (let i = 0; i < ADMIN_HASHES.length; i++) {
      if (constantTimeCompare(codeHash, ADMIN_HASHES[i])) { adminIndex = i; break; }
    }

    if (adminIndex !== -1) {
      const token = generateSessionToken();
      setSessionToken(token); sessionRef.current = token;
      setAccessCode(code);
      setMode("admin");
      setCurrentUser("Admin " + (adminIndex + 1));
      setCurrentUserId("admin_" + (adminIndex + 1));
      lastActivityRef.current = Date.now();
      setLoginError("");
      await saveLockout({ attempts: 0, lockUntil: 0 });
      addAuditEntry("AUTH_LOGIN_SUCCESS", { role: "admin" });
      return true;
    }

    // Check employee codes — team already loaded at init
    const emp = team.find(m => constantTimeCompare(m.codeHash, codeHash) && m.status === "Active");
    if (emp) {
      const token = generateSessionToken();
      setSessionToken(token); sessionRef.current = token;
      setAccessCode(code);
      setMode("employee");
      setCurrentUser(emp.name);
      setCurrentUserId(emp.id);
      lastActivityRef.current = Date.now();
      setLoginError("");
      await saveLockout({ attempts: 0, lockUntil: 0 });
      addAuditEntry("AUTH_LOGIN_SUCCESS", { role: "employee", user: emp.name });
      return true;
    }

    // FAILED
    const cl = (await loadLockout()) || { attempts: 0, lockUntil: 0 };
    cl.attempts += 1;
    if (cl.attempts >= MAX_LOGIN_ATTEMPTS) {
      cl.lockUntil = Date.now() + LOCKOUT_DURATION_MS;
      setLoginError("Too many attempts. Locked 5 min.");
      addAuditEntry("AUTH_LOCKOUT", { attempts: cl.attempts });
    } else {
      setLoginError("Invalid code. " + (MAX_LOGIN_ATTEMPTS - cl.attempts) + " left.");
      addAuditEntry("AUTH_LOGIN_FAILED", { attempts: cl.attempts });
    }
    await saveLockout(cl);
    return false;
  };

  const isValidSession = () => !!sessionToken && !!mode;

  // EXPENSES
  const addExpense = (data) => {
    if (!isValidSession()) return false;
    if (isThrottled("addExp")) { showToast("Too fast", "error"); return false; }
    const amount = parseFloat(data.amount);
    if (!validAmount(amount)) { showToast("Invalid amount", "error"); return false; }
    if (!validDate(data.date)) { showToast("Invalid date", "error"); return false; }
    const desc = sanitize(data.description);
    if (isDuplicateExpense(expenses, amount, desc)) { showToast("Duplicate detected", "error"); return false; }
    setExpenses(p => [...p, {
      id: uid(), date: data.date || today(), amount,
      category: sanitize(data.category), description: desc,
      paymentMethod: data.paymentMethod || "Cash",
      assignedTo: mode === "employee" ? currentUserId : "admin",
      createdAt: new Date().toISOString(),
    }]);
    addAuditEntry("EXPENSE_ADD", { amount, category: sanitize(data.category) });
    showToast("Expense added");
    return true;
  };

  const deleteExpense = (id) => {
    if (!isValidSession()) return false;
    const exp = expenses.find(e => e.id === id);
    if (!exp) return false;
    if (mode === "employee" && exp.assignedTo !== currentUserId) return false;
    setExpenses(p => p.filter(e => e.id !== id));
    addAuditEntry("EXPENSE_DELETE", { id, amount: exp.amount });
    showToast("Deleted", "warning");
    return true;
  };

  // TEAM
  const addTeamMember = async (data) => {
    if (!isValidSession() || mode !== "admin" || isThrottled("addTeam")) return null;
    const salary = parseFloat(data.salary);
    if (!validAmount(salary)) { showToast("Invalid salary", "error"); return null; }
    const name = sanitize(data.name).slice(0, MAX_NAME_LENGTH);
    if (!name) { showToast("Name required", "error"); return null; }
    const code = genCode();
    const codeHash = await sha256(code);
    const member = {
      id: uid(), name, role: sanitize(data.role).slice(0, 30), salary,
      salaryCurrency: data.salaryCurrency || "INR",
      salaryCycle: data.salaryCycle || "Monthly",
      salaryDate: data.salaryDate || today(),
      joinDate: data.joinDate || today(), status: "Active", codeHash,
      notes: sanitize(data.notes || "").slice(0, 200),
      createdAt: new Date().toISOString(),
    };
    setTeam(p => [...p, member]);
    addAuditEntry("TEAM_ADD", { name: member.name });
    showToast("Member added — " + member.name);
    return { member, code };
  };

  const removeTeamMember = (id) => {
    if (!isValidSession() || mode !== "admin") return false;
    const m = team.find(t => t.id === id);
    setTeam(p => p.map(t => t.id === id ? { ...t, status: "Inactive" } : t));
    addAuditEntry("TEAM_REMOVE", { name: m?.name });
    showToast((m?.name || "Member") + " deactivated", "warning");
    return true;
  };

  const deleteTeamMember = (id) => {
    if (!isValidSession() || mode !== "admin") return false;
    const m = team.find(t => t.id === id);
    setTeam(p => p.filter(t => t.id !== id));
    setPayments(p => p.filter(pay => pay.memberId !== id && pay.employeeId !== id));
    setLoanRequests(p => p.filter(l => l.employeeId !== id));
    addAuditEntry("TEAM_DELETE", { name: m?.name });
    showToast((m?.name || "Member") + " permanently deleted", "warning");
    return true;
  };

  const regenerateCode = async (id) => {
    if (!isValidSession() || mode !== "admin") return null;
    const code = genCode();
    const codeHash = await sha256(code);
    setTeam(p => p.map(m => m.id === id ? { ...m, codeHash } : m));
    addAuditEntry("CODE_REGEN", { memberId: id });
    return code;
  };

  // LOANS
  const requestLoan = (data) => {
    if (!isValidSession() || mode !== "employee" || isThrottled("loan")) return false;
    const pending = loanRequests.filter(l => l.employeeId === currentUserId && l.status === "Pending");
    if (pending.length >= MAX_PENDING_LOANS) { showToast("Max " + MAX_PENDING_LOANS + " pending", "error"); return false; }
    const amount = parseFloat(data.amount);
    if (!validAmount(amount)) { showToast("Invalid amount", "error"); return false; }
    const emp = team.find(m => m.id === currentUserId);
    if (emp && amount > emp.salary * SALARY_LOAN_MULTIPLIER) { showToast("Exceeds " + SALARY_LOAN_MULTIPLIER + "x salary", "error"); return false; }
    setLoanRequests(p => [...p, {
      id: uid(), employeeId: currentUserId, employee: currentUser,
      amount, reason: sanitize(data.reason), emi: parseFloat(data.emi) || Math.ceil(amount / 6),
      status: "Pending", createdAt: new Date().toISOString(),
    }]);
    addAuditEntry("LOAN_REQUEST", { amount });
    showToast("Loan submitted"); return true;
  };

  const approveLoan = (id) => {
    if (!isValidSession() || mode !== "admin") return false;
    setLoanRequests(p => p.map(l => l.id === id ? { ...l, status: "Approved" } : l));
    addAuditEntry("LOAN_APPROVE", { loanId: id }); showToast("Approved"); return true;
  };

  const denyLoan = (id) => {
    if (!isValidSession() || mode !== "admin") return false;
    setLoanRequests(p => p.map(l => l.id === id ? { ...l, status: "Denied" } : l));
    addAuditEntry("LOAN_DENY", { loanId: id }); showToast("Denied", "warning"); return true;
  };

  const deleteLoan = (id) => {
    if (!isValidSession() || mode !== "admin") return false;
    const loan = loanRequests.find(l => l.id === id);
    setLoanRequests(p => p.filter(l => l.id !== id));
    addAuditEntry("LOAN_DELETE", { loanId: id, employee: loan?.employee, amount: loan?.amount });
    showToast("Loan request deleted", "warning");
    return true;
  };

  // CRYPTO
  const addHolding = (data) => {
    if (!isValidSession() || mode !== "admin" || isThrottled("addH")) return false;
    const amount = parseFloat(data.amount);
    if (!amount || amount <= 0) { showToast("Invalid", "error"); return false; }
    setCryptoHoldings(p => [...p, {
      id: uid(), coinId: data.coinId, symbol: data.symbol, name: data.name, amount,
      source: data.source || "Stake Payout", dateReceived: data.dateReceived || today(),
      priceAtReceipt: parseFloat(data.priceAtReceipt) || 0,
      notes: sanitize(data.notes || ""), createdAt: new Date().toISOString(),
    }]);
    addAuditEntry("CRYPTO_ADD", { coin: data.symbol, amount }); showToast(data.symbol + " added"); return true;
  };

  const removeHolding = (id) => {
    if (!isValidSession() || mode !== "admin") return false;
    setCryptoHoldings(p => p.filter(h => h.id !== id));
    addAuditEntry("CRYPTO_REMOVE", { holdingId: id }); showToast("Removed", "warning"); return true;
  };

  const recordPayment = (data) => {
    if (!isValidSession() || mode !== "admin") return false;
    setPayments(p => [...p, {
      id: uid(), memberId: data.memberId, month: data.month || currentMonth(),
      amount: parseFloat(data.amount), currency: data.currency || "INR",
      status: "Paid", paidDate: today(), txHash: data.txHash || "",
    }]);
    addAuditEntry("PAYMENT_RECORD", { member: data.memberName, amount: parseFloat(data.amount) }); showToast("Payment recorded"); return true;
  };


  
  const repayLoan = (loanId) => {
    setLoanRequests(prev => prev.map(l => 
      l.id === loanId ? { ...l, repaid: true, repaidAt: new Date().toISOString() } : l
    ));
  };

  const restoreBackup = (data) => {
    if (data.expenses) setExpenses(data.expenses);
    if (data.team) setTeam(data.team);
    if (data.cryptoHoldings) setCryptoHoldings(data.cryptoHoldings);
    if (data.loanRequests) setLoanRequests(data.loanRequests);
    if (data.payments) setPayments(data.payments);
  };

  return (
    <Ctx.Provider value={{
      mode, accessCode, sessionToken, currentUser, currentUserId, loginError, isLoading, toast,
      auditIntegrity, sidebarOpen, setSidebarOpen,
      login, logout, isValidSession, showToast,
      expenses, team, cryptoHoldings, auditLog, loanRequests, payments, repayLoan, restoreBackup,
      addExpense, deleteExpense, addTeamMember, removeTeamMember, deleteTeamMember, regenerateCode,
      requestLoan, approveLoan, denyLoan, deleteLoan, addHolding, removeHolding, recordPayment,
      currency, setCurrency,
    }}>{children}</Ctx.Provider>
  );
}
